import { transformStaticTextToI18n } from '~/utils/i18nHelpers'

/**
 * Initialize i18n with dynamic configuration
 */
export async function initializeI18n(data) {
  try {
    const { $i18n } = useNuxtApp()
    const translationsStore = useTranslationsStore()
    const dataStore = useDataAll()

    if (!$i18n) {
      console.warn('i18n instance not available yet')
      return
    }

    if (translationsStore.isInitialized) {
      console.info('Translation system already initialized')
      return
    }

    translationsStore.setLoading(true)

    // Setup locales from language data
    await setupDynamicLocales($i18n, dataStore, translationsStore)

    // Load translations
    await loadTranslations(data, $i18n, dataStore, translationsStore)

    // Set current locale
    await setCurrentLocale($i18n, dataStore, translationsStore)

    translationsStore.setInitialized(true)

  } catch (error) {
    console.error('Failed to initialize translation system:', error)
    throw error // Re-throw to let caller handle it
  } finally {
    try {
      const translationsStore = useTranslationsStore()
      translationsStore.setLoading(false)
    } catch (e) {
      console.warn('Could not set loading state to false:', e)
    }
  }
}

/**
 * Setup dynamic locales from API data
 */
async function setupDynamicLocales(i18n, dataStore, translationsStore) {
  const languages = dataStore.getLanguages()
  const defaultLanguage = dataStore.getDefaultLanguage()

  if (!languages?.length) {
    throw new Error('No languages available from data store')
  }



  // Create dynamic locales
  const dynamicLocales = languages.map(lang => ({
    code: lang.code,
    name: lang.name || lang.code.toUpperCase(),
    file: null
  }))

  // Ensure i18n options exist
  if (!i18n.options) {
    console.warn('i18n.options is undefined, creating options object')
    i18n.options = {}
  }

  // Update i18n configuration safely
  try {
    i18n.options.locales = dynamicLocales
    i18n.options.defaultLocale = defaultLanguage

    if (i18n.defaultLocale !== undefined) {
      i18n.defaultLocale = defaultLanguage
    }

    // Update available locales if the method exists
    if (typeof i18n.setAvailableLocales === 'function') {
      i18n.setAvailableLocales(dynamicLocales.map(locale => locale.code))
    } else {
      console.warn('setAvailableLocales method not available on i18n instance')
    }
  } catch (error) {
    console.error('Error setting up i18n configuration:', error)
    throw error
  }

  // Update store
  translationsStore.setAvailableLanguages(languages)
  translationsStore.setDefaultLanguage(defaultLanguage)
}

/**
 * Load translations for all or specific languages
 */
async function loadTranslations(data, i18n, dataStore, translationsStore) {
  if (!data?.StaticText) {
    console.warn('No StaticText found in data')
    return
  }


  // Check if StaticText contains multiple languages or single language
  if (isMultiLanguageStructure(data.StaticText, dataStore)) {
    await loadMultiLanguageTranslations(data.StaticText, i18n, dataStore, translationsStore)
  } else {
    await loadSingleLanguageTranslations(data.StaticText, i18n, dataStore, translationsStore)
  }
}

/**
 * Check if StaticText contains multiple languages
 */
function isMultiLanguageStructure(staticText, dataStore) {
  if (typeof staticText !== 'object' || Array.isArray(staticText)) {
    return false
  }

  const languages = dataStore.getLanguages()

  if (!languages?.length) return false

  const languageCodes = languages.map(lang => lang.code)

  // Check if top-level keys match language codes
  return Object.keys(staticText).some(key => languageCodes.includes(key))
}

/**
 * Load translations for multiple languages
 */
async function loadMultiLanguageTranslations(staticText, i18n, dataStore, translationsStore) {
  const languages = dataStore.getLanguages()

  for (const lang of languages) {
    if (staticText[lang.code]) {
      try {
        const i18nMessages = transformStaticTextToI18n(staticText[lang.code])
        i18n.setLocaleMessage(lang.code, i18nMessages)
        translationsStore.markLanguageAsLoaded(lang.code)
      } catch (error) {
        console.error(`Error loading translations for ${lang.code}:`, error)
      }
    }
  }
}

/**
 * Load translations for single language (current)
 */
async function loadSingleLanguageTranslations(staticText, i18n, dataStore, translationsStore) {
  const currentLanguage = dataStore.getLanguage()

  if (currentLanguage) {
    try {
      const i18nMessages = transformStaticTextToI18n(staticText)
      i18n.setLocaleMessage(currentLanguage, i18nMessages)
      translationsStore.markLanguageAsLoaded(currentLanguage)
    } catch (error) {
      console.error(`Error loading translations for current language ${currentLanguage}:`, error)
    }
  }

  // Load missing languages if needed
  await loadMissingLanguages(i18n, dataStore, translationsStore)
}

/**
 * Load missing language translations
 */
async function loadMissingLanguages(i18n, dataStore, translationsStore) {
  const languages = dataStore.getLanguages()
  const currentLanguage = dataStore.getLanguage()

  for (const lang of languages) {
    if (lang.code !== currentLanguage && !translationsStore.isLanguageLoaded(lang.code)) {
      try {
        await loadLanguageSpecific(lang.code, i18n, dataStore, translationsStore)
      } catch (error) {
        console.warn(`Failed to load translations for ${lang.code}:`, error)
      }
    }
  }
}

/**
 * Load translations for a specific language
 */
async function loadLanguageSpecific(languageCode, i18n, dataStore, translationsStore) {
  const { dataRepository } = await import('~/repositories/dataRepository')
  const label = dataStore.getLabel()
  const hostName = dataStore.getDataVar('hostname')

  try {
    const fetchResult = await dataRepository.fetchAllData(languageCode, label, hostName)
    const data = fetchResult.data?.value

    if (data?.StaticText) {
      const i18nMessages = transformStaticTextToI18n(data.StaticText)
      i18n.setLocaleMessage(languageCode, i18nMessages)
      translationsStore.markLanguageAsLoaded(languageCode)
    }
  } catch (error) {
    console.error(`Error loading translations for ${languageCode}:`, error)
  }
}

/**
 * Set current locale in i18n
 */
async function setCurrentLocale(i18n, dataStore, translationsStore) {
  const currentLanguage = dataStore.getLanguage() || dataStore.getDefaultLanguage()

  if (!currentLanguage) {
    console.warn('No current language available')
    return
  }

  try {
    // Check if locale is available
    const availableLocales = i18n.availableLocales || []

    if (availableLocales.length === 0 || availableLocales.includes(currentLanguage)) {
      await i18n.setLocale(currentLanguage)
      translationsStore.setCurrentLanguage(currentLanguage)
    } else {
      console.warn(`Language ${currentLanguage} not available in i18n locales:`, availableLocales)
    }
  } catch (error) {
    console.error(`Error setting current locale to ${currentLanguage}:`, error)
  }
}

/**
 * Switch language dynamically
 */
export async function switchLanguage(languageCode) {
  const { $i18n } = useNuxtApp()
  const translationsStore = useTranslationsStore()
  const dataStore = useDataAll()

  if (!$i18n) {
    console.error('i18n instance not available')
    return
  }

  try {
    // Load translations if not already loaded
    if (!translationsStore.isLanguageLoaded(languageCode)) {
      await loadLanguageSpecific(languageCode, $i18n, dataStore, translationsStore)
    }

    // Switch locale
    await $i18n.setLocale(languageCode)
    translationsStore.setCurrentLanguage(languageCode)
  } catch (error) {
    console.error(`Failed to switch to language ${languageCode}:`, error)
  }
}
